<template>
  <div class="min-h-screen bg-gray-50 flex flex-col">
    <!-- Hero Section -->
    <section class="relative overflow-hidden bg-gradient-to-br from-emerald-500 via-teal-500 to-fuchsia-600 py-12 md:py-16">
      <div class="absolute inset-0">
        <img
          src="https://images.ctfassets.net/zhgoh1a85x2k/7qiZ6sqBkl7IS1AXUA5j4T/d3ab432178763cbc8f0a053c4bbb320b/mtu-image.webp"
          alt=""
          class="w-full h-full object-cover object-center"
        >
      </div>
      <div class="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/60 to-transparent"></div>

      <div class="relative z-10 px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 2xl:px-32 pt-[80px] md:pt-[120px] pb-12">
        <div class="flex flex-col md:flex-row items-start md:items-center gap-8 max-w-6xl mx-auto">
          <div class="text-white md:w-1/2">
            <p class="text-emerald-400 font-semibold tracking-widest uppercase text-sm mb-4">
              Restez connecté
            </p>
            <h1 class="text-4xl lg:text-6xl xl:text-7xl font-bold leading-tight mb-4">
              Rechargez<br>
              <span class="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-400 to-amber-400">
                instantanément
              </span>
            </h1>
            <p class="text-lg lg:text-xl text-white/80">
              dans plus de 160 pays
            </p>
          </div>

          <div class="w-full md:w-[400px] md:ml-auto backdrop-blur-xl">
            <div class="bg-white/10 border border-white/20 rounded-3xl p-6 shadow-2xl">
              <p class="text-white font-semibold mb-4 flex items-center gap-2">
                <svg class="w-5 h-5 text-fuchsia-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/>
                </svg>
                Choisissez le pays
              </p>

              <!-- Country Search -->
              <div class="relative mb-4">
                <input
                  v-model="countrySearch"
                  type="text"
                  placeholder="Bénin, Côte d'Ivoire..."
                  @focus="isSearchFocused = true"
                  @blur="isSearchFocused = false"
                  class="w-full px-5 py-4 bg-white border-0 rounded-2xl text-gray-900 font-semibold placeholder:text-gray-400 focus:outline-none focus:ring-4 focus:ring-fuchsia-400/30 transition-all"
                >
                <div v-if="filteredCountries.length > 0" class="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl max-h-60 overflow-y-auto z-50">
                  <button
                    v-for="country in filteredCountries"
                    :key="country.countryCode"
                    @click="selectCountry(country)"
                    class="w-full px-5 py-3 text-left hover:bg-gray-50 flex items-center gap-3 first:rounded-t-2xl"
                    :class="{ 'bg-fuchsia-50': selectedCountry?.countryCode === country.countryCode }"
                  >
                    <img v-if="country.localLogoUrl" :src="country.localLogoUrl" :alt="country.countryName" class="w-8 h-8 object-contain">
                    <svg v-else class="w-8 h-8 text-fuchsia-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/>
                    </svg>
                    <span class="font-semibold text-gray-900">{{ country.countryName }}</span>
                  </button>
                </div>
              </div>

              <!-- Selected Country Display -->
              <div v-if="selectedCountry" class="mb-4 p-3 bg-white/20 rounded-xl flex items-center gap-3">
                <img v-if="selectedCountry.localLogoUrl" :src="selectedCountry.localLogoUrl" :alt="selectedCountry.countryName" class="w-10 h-10 object-contain">
                <svg v-else class="w-10 h-10 text-fuchsia-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/>
                </svg>
                <div class="text-white">
                  <p class="font-bold">{{ selectedCountry.countryName }}</p>
                  <p class="text-sm text-white/70">{{ selectedCountry.currency }}</p>
                </div>
                <button @click="clearCountrySelection" class="ml-auto text-white/70 hover:text-white">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>

              <button
                @click="goToMobileTopUp"
                class="group w-full py-4 bg-gradient-to-r from-fuchsia-500 to-amber-500 text-white font-bold rounded-2xl hover:shadow-2xl hover:shadow-fuchsia-500/30 hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2"
              >
                Recharger
                <svg class="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <main class="flex-1">
      <div class="w-full">
        <!-- Trust Bar -->
        <div class="py-4 bg-white border-b border-gray-100">
          <div class="mx-auto max-w-7xl lg:max-w-[1600px] px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24">
            <div class="flex justify-around items-center gap-6">
              <div class="flex items-center gap-2 text-sm">
                <svg class="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
                <span class="font-semibold">Revendeur certifié</span>
              </div>
              <div class="flex items-center gap-2 text-sm">
                <svg class="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"/>
                </svg>
                <span class="font-semibold">Paiement sécurisé</span>
              </div>
              <div class="flex items-center gap-2 text-sm">
                <svg class="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
                  <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z"/>
                </svg>
                <span class="font-semibold">Livraison instantanée</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Most Popular Section -->
        <section class="py-12 md:py-16 bg-white">
          <div class="mx-auto max-w-7xl lg:max-w-[1600px] px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24">
            <!-- Category Tabs -->
            <div class="flex flex-wrap gap-3 pb-6">
              <button
                v-for="cat in popularCategories"
                :key="cat.key"
                @click="selectedPopularCategory = cat.key"
                class="flex items-center gap-2 px-5 py-3 rounded-2xl whitespace-nowrap transition-all font-semibold text-sm"
                :class="selectedPopularCategory === cat.key ? 'bg-gray-900 text-white shadow-lg' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
              >
                <span v-html="cat.icon"></span>
                <span>{{ cat.label }}</span>
              </button>
            </div>

            <!-- Products Grid -->
            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6 py-4">
              <a
                v-for="product in filteredPopularProducts"
                :key="product.id"
                @click="handlePopularProductClick(product)"
                class="flex flex-col gap-2 cursor-pointer group"
              >
                <div class="aspect-[188/148] bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl overflow-hidden shadow-[0_0_4px_rgba(0,0,0,0.25)] transition-transform group-hover:scale-105">
                  <img
                    :alt="product.name"
                    loading="lazy"
                    class="w-full h-full object-cover"
                    :src="product.image"
                  >
                </div>
                <p class="font-body font-bold text-sm text-gray-900 text-center line-clamp-2">{{ product.name }}</p>
              </a>
            </div>
          </div>
        </section>

        <!-- Description Banner -->
        <div class="py-10 bg-gradient-to-r from-gray-900 to-emerald-700">
          <div class="mx-auto max-w-7xl lg:max-w-[1600px] px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24">
            <h1 class="text-xl md:text-2xl lg:text-3xl font-bold text-white text-center">
              <span class="text-fuchsia-400">Fluxreload</span> est la plus grande boutique en ligne de cartes de paiement, de cartes cadeaux et de recharges mobiles.
            </h1>
          </div>
        </div>

        <!-- Category Sections -->
        <section class="py-12 md:py-20 bg-white">
          <div class="mx-auto max-w-7xl lg:max-w-[1600px] px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24">
            <div class="grid gap-12 md:gap-16">
              <div
                v-for="section in categorySections"
                :key="section.key"
                v-show="section.products && section.products.length > 0"
              >
                <!-- Section Header -->
                <div class="flex flex-wrap justify-between items-center gap-4 mb-6">
                  <h2 class="font-body text-2xl md:text-4xl font-bold text-black">{{ section.title }}</h2>
                  <button
                    @click="goToSection(section.link)"
                    class="hidden md:flex items-center justify-between gap-0 px-4 py-3 bg-white border border-gray-200 rounded-full font-bold text-gray-900 hover:bg-gray-50 transition-colors min-w-16 h-12"
                  >
                    Toutes
                    <svg role="img" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" class="stroke-gray-900">
                      <path d="M9 4.99963L16 11.9996L9 18.9996" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" stroke="currentColor" fill="none"/>
                    </svg>
                  </button>
                </div>

                <!-- Products -->
                <!-- eSim Packages - Special Design -->
                <div v-if="section.key === 'esim'" class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 py-4">
                  <div
                    v-for="product in section.products.slice(0, 6)"
                    :key="product.id"
                    @click="handleProductClick(product, section.key)"
                    class="package-card cursor-pointer group"
                  >
                    <div class="package-card-inner">
                      <div class="package-header">
                        <div class="package-data">
                          {{ formatEsimData(product.data.volume) }}
                        </div>
                        <div class="package-duration">{{ product.data.duration }} jours</div>
                      </div>
                      <div class="package-name">{{ product.name }}</div>
                      <div class="package-details">
                        <span class="package-speed">{{ product.data.speed || '3G/4G' }}</span>
                      </div>
                      <div class="package-price">
                        {{ formatEsimPrice(product.data.price) }}
                      </div>
                      <button class="package-button">
                        Choisir
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Regular Products - Standard Design -->
                <div v-else class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6 py-4">
                  <a
                    v-for="product in section.products.slice(0, 6)"
                    :key="product.id"
                    @click="handleProductClick(product, section.key)"
                    class="flex flex-col gap-2 cursor-pointer group"
                  >
                    <div class="aspect-[188/148] bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl overflow-hidden shadow-[0_0_4px_rgba(0,0,0,0.25)] transition-transform group-hover:scale-105">
                      <img
                        :alt="product.name"
                        loading="lazy"
                        class="w-full h-full object-cover"
                        :src="product.image"
                      >
                    </div>
                    <p class="font-body font-bold text-sm text-gray-900 text-center line-clamp-2">{{ product.name }}</p>
                  </a>
                </div>

                <!-- Mobile "Toutes" button -->
                <button
                  v-if="section.link"
                  @click="goToSection(section.link)"
                  class="md:hidden mt-6 flex items-center justify-between gap-0 w-full px-4 py-3 bg-white border border-gray-200 rounded-full font-bold text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  Toutes
                  <svg role="img" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" class="stroke-gray-900">
                    <path d="M9 4.99963L16 11.9996L9 18.9996" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" stroke="currentColor" fill="none"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router';
import { Countries } from '@features/shared/libraries/countries';

const router = useRouter();
const {proxy} = getCurrentInstance()
const {$stores} = proxy

// Country search
const countrySearch = ref('');
const selectedCountry = ref(null);
const isSearchFocused = ref(false);

// Popular Products Category
const selectedPopularCategory = ref('all');

const popularCategories = [
  { key: 'all', label: 'Toutes', icon: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/></svg>' },
  { key: 'payment', label: 'Cartes de paiement', icon: '<img src="https://recharge-prd.asset.akeneo.cloud/category_assets/media/payment_card_icon.svg" class="w-5 h-5">' },
  { key: 'entertainment', label: 'Divertissement', icon: '<img src="https://recharge-prd.asset.akeneo.cloud/category_assets/media/entertainment_icon.svg" class="w-5 h-5">' },
  { key: 'shopping', label: 'Shopping', icon: '<img src="https://recharge-prd.asset.akeneo.cloud/category_assets/media/shopping_icon.svg" class="w-5 h-5">' },
  { key: 'gaming', label: 'Jeux vidéo', icon: '<img src="https://recharge-prd.asset.akeneo.cloud/category_assets/media/gaming_icon.svg" class="w-5 h-5">' },
];

// Loading states
const loading = ref({
  giftCards: true,
  mobileOperators: true,
  esimPackages: true
});
const error = ref({
  giftCards: null,
  mobileOperators: null,
  esimPackages: null
});

// Data stores
const giftCards = ref([]);
const mobileOperators = ref([]);
const esimPackages = ref([]);

// Category config for gift cards (matching GiftCardsPage)
const categoryConfig = {
  'shopping-gift-cards': { categoryId: 5, categoryKey: 'shopping', label: 'Shopping' },
  'gaming-cards': { categoryId: 3, categoryKey: 'gaming', label: 'Jeux vidéo' },
  'payment-cards': { categoryId: 1, categoryKey: 'payment', label: 'Cartes de paiement' },
  'entertainment-gift-cards': { categoryId: 4, categoryKey: 'entertainment', label: 'Divertissement' },
  'other-cards': { categoryId: null, categoryKey: 'other', label: 'Autres' }
};

// Helper: Get category key from product
const categoryKeyFromProduct = (product) => {
  const catId = product.category?.id;
  const cat = Object.values(categoryConfig).find(c => c.categoryId === catId);
  return cat?.categoryKey || 'shopping';
};

// Computed: Filter popular products by selected category
const filteredPopularProducts = computed(() => {
  let products = [];

  if (selectedPopularCategory.value === 'all') {
    products = giftCards.value.slice(0, 6);
  } else {
    const cat = Object.values(categoryConfig).find(c => c.categoryKey === selectedPopularCategory.value);
    if (cat) {
      if (cat.categoryId) {
        // Specific category: show only products in this category
        products = giftCards.value.filter(p => p.category?.id === cat.categoryId);
      } else {
        // "Other" category: show products NOT in main categories (1, 3, 4, 5)
        const mainCategoryIds = [1, 3, 4, 5];
        products = giftCards.value.filter(p => !mainCategoryIds.includes(p.category?.id));
      }
    }
  }

  // Map to format expected by template
  return products.slice(0, 6).map(p => ({
    id: p.productId,
    name: p.productName,
    category: categoryKeyFromProduct(p),
    image: p.logoUrls?.[0] || '',
    data: p
  }));
});

// Computed: Filter countries for search
const filteredCountries = computed(() => {
  // Show all countries when focused, even if search is empty
  if (isSearchFocused.value && !countrySearch.value) {
    return Countries.get();
  }
  // Show filtered results when searching
  if (countrySearch.value) {
    const search = countrySearch.value.toLowerCase();
    return Countries.get().filter(c =>
      c.countryName?.toLowerCase().includes(search) ||
      c.countryCode?.toLowerCase().includes(search)
    );
  }
  // Otherwise return empty
  return [];
});

// Computed: Category sections with dynamic data
const categorySections = computed(() => [
  {
    key: 'mobile',
    title: 'Recharge mobile',
    link: '/mobile-top-up',
    products: mobileOperators.value.slice(0, 6).map(op => ({
      id: op.operatorId,
      name: op.name || op.operatorName,
      image: op.logoUrls?.[1] || op.logoUrls?.[0] || '',
      data: op
    }))
  },
  {
    key: 'payment',
    title: 'Cartes de paiement',
    link: '/cards/payment-cards',
    products: giftCards.value
      .filter(p => p.category?.id === 1)
      .slice(0, 6)
      .map(p => ({
        id: p.productId,
        name: p.productName,
        image: p.logoUrls?.[0] || '',
        data: p
      }))
  },
  {
    key: 'entertainment',
    title: 'Divertissement',
    link: '/cards/entertainment-gift-cards',
    products: giftCards.value
      .filter(p => p.category?.id === 4)
      .slice(0, 6)
      .map(p => ({
        id: p.productId,
        name: p.productName,
        image: p.logoUrls?.[0] || '',
        data: p
      }))
  },
  {
    key: 'shopping',
    title: 'Shopping',
    link: '/cards/shopping-gift-cards',
    products: giftCards.value
      .filter(p => p.category?.id === 5)
      .slice(0, 6)
      .map(p => ({
        id: p.productId,
        name: p.productName,
        image: p.logoUrls?.[0] || '',
        data: p
      }))
  },
  {
    key: 'gaming',
    title: 'Jeux vidéo',
    link: '/cards/gaming-cards',
    products: giftCards.value
      .filter(p => p.category?.id === 3)
      .slice(0, 6)
      .map(p => ({
        id: p.productId,
        name: p.productName,
        image: p.logoUrls?.[0] || '',
        data: p
      }))
  },
  {
    key: 'other',
    title: 'Autres cartes',
    link: '/cards/other-cards',
    products: giftCards.value
      .filter(p => {
        const mainCategoryIds = [1, 3, 4, 5];
        return !mainCategoryIds.includes(p.category?.id);
      })
      .slice(0, 6)
      .map(p => ({
        id: p.productId,
        name: p.productName,
        image: p.logoUrls?.[0] || '',
        data: p
      }))
  },
  {
    key: 'esim',
    title: 'eSim - Voyagez connecté',
    link: '/esim',
    products: esimPackages.value.slice(0, 4).map(pkg => ({
      id: pkg.packageCode,
      name: pkg.name || `${pkg.volume ? formatData(pkg.volume) : 'Illimité'} - ${pkg.duration} jours`,
      image: '',
      data: pkg
    }))
  },
]);

// Select country in hero
const selectCountry = (country) => {
  selectedCountry.value = country;
  countrySearch.value = '';
  isSearchFocused.value = false;
  // Sync with store
  $stores.global.selectCountry(country.countryCode);
};

// Clear country selection
const clearCountrySelection = () => {
  selectedCountry.value = null;
  $stores.global.clearCountrySelection();
};

// Fetch gift cards
const fetchGiftCards = async () => {
  const country = selectedCountry.value || $stores.global.state.selectedCountry;
  if (!country) {
    giftCards.value = [];
    return;
  }

  loading.value.giftCards = true;
  error.value.giftCards = null;

  try {
    const { bus } = await import('@core/events/eventSystem');

    return new Promise((resolve, reject) => {
      const successHandler = (data) => {
        bus.off('cards.fetch_by_country_success', successHandler);
        bus.off('cards.fetch_by_country_error', errorHandler);
        giftCards.value = data.data || data || [];
        loading.value.giftCards = false;
        resolve();
      };

      const errorHandler = (err) => {
        bus.off('cards.fetch_by_country_success', successHandler);
        bus.off('cards.fetch_by_country_error', errorHandler);
        if (err?.response?.status === 404 || err?.status === 404) {
          giftCards.value = [];
        } else {
          error.value.giftCards = 'Erreur lors du chargement des cartes cadeaux';
        }
        loading.value.giftCards = false;
        reject(err);
      };

      bus.on('cards.fetch_by_country_success', successHandler);
      bus.on('cards.fetch_by_country_error', errorHandler);

      bus.emit('cards.fetch_by_country', country.countryCode);
    });
  } catch (e) {
    error.value.giftCards = 'Erreur lors du chargement des cartes cadeaux';
    loading.value.giftCards = false;
  }
};

// Fetch mobile operators
const fetchMobileOperators = async () => {
  const country = selectedCountry.value || $stores.global.state.selectedCountry;
  if (!country) {
    mobileOperators.value = [];
    return;
  }

  loading.value.mobileOperators = true;
  error.value.mobileOperators = null;

  try {
    const { bus } = await import('@core/events/eventSystem');

    return new Promise((resolve, reject) => {
      const successHandler = (data) => {
        bus.off('products.fetch_operators_success', successHandler);
        bus.off('products.fetch_operators_error', errorHandler);
        mobileOperators.value = data.data?.operatorList || data.data || data || [];
        loading.value.mobileOperators = false;
        resolve();
      };

      const errorHandler = (err) => {
        bus.off('products.fetch_operators_success', successHandler);
        bus.off('products.fetch_operators_error', errorHandler);
        if (err?.response?.status === 404 || err?.status === 404) {
          mobileOperators.value = [];
        } else {
          error.value.mobileOperators = 'Erreur lors du chargement des opérateurs';
        }
        loading.value.mobileOperators = false;
        reject(err);
      };

      bus.on('products.fetch_operators_success', successHandler);
      bus.on('products.fetch_operators_error', errorHandler);

      bus.emit('products.fetch_operators', {
        countryCode: country.countryCode,
        type: 'airtime'
      });
    });
  } catch (e) {
    error.value.mobileOperators = 'Erreur lors du chargement des opérateurs';
    loading.value.mobileOperators = false;
  }
};

// Fetch eSim packages
const fetchEsimPackages = async () => {
  const country = selectedCountry.value || $stores.global.state.selectedCountry;
  if (!country) {
    esimPackages.value = [];
    return;
  }

  loading.value.esimPackages = true;
  error.value.esimPackages = null;

  try {
    const { bus } = await import('@core/events/eventSystem');
    const constants = await import('@features/esim/constants');

    // Map country code to esim location code
    const getEsimLocationCode = (countryCode) => {
      const mapping = {
        'BJ': 'BJ', 'TG': 'TG', 'CI': 'CI', 'SN': 'SN', 'ML': 'ML',
        'FR': 'FR', 'BE': 'BE', 'CH': 'CH', 'CM': 'CM', 'CD': 'CD',
        'NE': 'NE', 'BF': 'BF', 'GN': 'GN', 'GA': 'GA', 'CG': 'CG',
        'CF': 'CF', 'TD': 'TD', 'MG': 'MG', 'RW': 'RW', 'BI': 'BI',
        'DJ': 'DJ', 'ER': 'ER', 'ET': 'ET', 'KE': 'KE', 'MG': 'MG',
        'MW': 'MW', 'MU': 'MU', 'MZ': 'MZ', 'NA': 'NA', 'RW': 'RW',
        'SC': 'SC', 'TZ': 'TZ', 'UG': 'UG', 'ZM': 'ZM', 'ZW': 'ZW',
        'AO': 'AO', 'NA': 'NA', 'ZA': 'ZA', 'EG': 'EG', 'MA': 'MA',
        'TN': 'TN', 'DZ': 'DZ', 'LY': 'LY', 'NG': 'NG', 'GH': 'GH',
        'CI': 'CI', 'SN': 'SN', 'ML': 'ML', 'BF': 'BF', 'NE': 'NE',
        'TD': 'TD', 'CF': 'CF', 'CD': 'CD', 'CG': 'CG', 'GA': 'GA',
        'CM': 'CM', 'LR': 'LR', 'SL': 'SL', 'GN': 'GN', 'GW': 'GW',
        'GM': 'GM', 'MR': 'MR', 'SO': 'SO', 'SD': 'SD', 'ET': 'ET',
        'KE': 'KE', 'UG': 'UG', 'TZ': 'TZ', 'RW': 'RW', 'BI': 'BI',
        'MW': 'MW', 'ZM': 'ZM', 'MZ': 'MZ', 'ZW': 'ZW', 'BW': 'BW',
        'NA': 'NA', 'SZ': 'SZ', 'LS': 'LS', 'ZA': 'ZA'
      };
      return mapping[countryCode] || countryCode;
    };

    const locationCode = getEsimLocationCode(country.countryCode);

    return new Promise((resolve, reject) => {
      const successHandler = (data) => {
        bus.off(constants.EVENTS.ESIM.FETCH_PACKAGES_SUCCESS, successHandler);
        bus.off(constants.EVENTS.ESIM.FETCH_PACKAGES_ERROR, errorHandler);
        esimPackages.value = data || [];
        loading.value.esimPackages = false;
        resolve();
      };

      const errorHandler = (err) => {
        bus.off(constants.EVENTS.ESIM.FETCH_PACKAGES_SUCCESS, successHandler);
        bus.off(constants.EVENTS.ESIM.FETCH_PACKAGES_ERROR, errorHandler);
        esimPackages.value = [];
        loading.value.esimPackages = false;
        reject(err);
      };

      bus.on(constants.EVENTS.ESIM.FETCH_PACKAGES_SUCCESS, successHandler);
      bus.on(constants.EVENTS.ESIM.FETCH_PACKAGES_ERROR, errorHandler);

      bus.emit(constants.EVENTS.ESIM.FETCH_PACKAGES, locationCode);
    });
  } catch (e) {
    esimPackages.value = [];
    loading.value.esimPackages = false;
  }
};

// Format data volume for display
const formatData = (volume) => {
  if (!volume) return 'Illimité';
  const gb = volume / (1024 * 1024 * 1024);
  if (gb >= 1) return `${Math.round(gb)}GB`;
  const mb = volume / (1024 * 1024);
  return `${Math.round(mb)}MB`;
};

// Format eSim data volume for display (alias)
const formatEsimData = (volume) => {
  if (!volume) return 'Illimité';
  const gb = volume / (1024 * 1024 * 1024);
  if (gb >= 1) return `${Math.round(gb)}GB`;
  const mb = volume / (1024 * 1024);
  return `${Math.round(mb)}MB`;
};

// Format eSim price for display
const formatEsimPrice = (price) => {
  if (!price) return '---';
  // API returns prices in cents, divide by 100
  const priceInUnits = price / 100;
  // Get currency from selected country, default to XOF
  const currency = selectedCountry.value?.currency || 'XOF';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0
  }).format(priceInUnits);
};

// Watch store changes - declared BEFORE onMounted to catch initial state
watch(() => $stores.global.state.selectedCountry, (newCountry) => {
  selectedCountry.value = newCountry;
  // Reload data when country changes
  if (newCountry) {
    fetchGiftCards();
    fetchMobileOperators();
    fetchEsimPackages();
  }
}, { immediate: true });

// Load all data on mount
onMounted(async () => {
  await Promise.all([
    fetchGiftCards(),
    fetchMobileOperators(),
    fetchEsimPackages()
  ]);

  // If no country selected in store, select Benin by default
  if (!$stores.global.state.selectedCountry) {
    const defaultCountry = Countries.find('BJ');
    if (defaultCountry) {
      $stores.global.selectCountry('BJ');
    }
  }
});

const goToMobileTopUp = () => {
  router.push('/mobile-top-up');
};

const goToSection = (link) => {
  router.push(link);
};

// Handle product click - routes to product detail pages
const handleProductClick = async (product, categoryKey) => {
  const country = selectedCountry.value || $stores.global.state.selectedCountry;

  switch (categoryKey) {
    case 'mobile':
      await handleMobileTopUpClick(product);
      break;
    case 'payment':
    case 'shopping':
    case 'gaming':
    case 'entertainment':
      const productData = product.data || product;

      // Check if this is a custom card
      if (productData.isCustom || (typeof productData.productId === 'string' && productData.productId.startsWith('card_'))) {
        router.push(`/custom-cards/${productData.productId}`);
        return;
      }

      // Redirect to gift card product detail page
      const categorySlug = categorySlugMap[categoryKey] || 'shopping-gift-cards';
      const slug = `${productData.productId}-${country?.countryCode?.toLowerCase() || 'bj'}`;
      router.push(`/cards/${categorySlug}/${slug}`);
      break;
    case 'esim':
      await handleEsimClick(product);
      break;
    default:
      if (product.data) {
        router.push(`/cards/${product.data.slug || product.data.productId}`);
      }
  }
};

// Handle mobile top-up click - redirects to product detail page
const handleMobileTopUpClick = async (product) => {
  const operatorData = product.data || product;
  const country = selectedCountry.value || $stores.global.state.selectedCountry;

  router.push({
    path: `/mobile-top-up/${product.id || operatorData.operatorId || 1}-${country?.countryCode || 'BJ'}`
  });
};

// Handle payment card click - opens amount dialog
const handlePaymentCardClick = async (product) => {
  const productData = product.data || product;

  const fixedDenominations = productData.denominationType === 'FIXED'
    ? (productData.fixedSenderDenominations || []).map((val, idx) => ({
        value: val,
        recipientValue: productData.fixedRecipientDenominations?.[idx] || ''
      }))
    : [];

  const config = {
    title: `Acheter ${product.name}`,
    message: 'Sélectionnez le montant',
    product: productData,
    denominationType: productData.denominationType,
    fixedDenominations: fixedDenominations,
    minAmount: productData.minSenderDenomination,
    maxAmount: productData.maxSenderDenomination,
    exchangeRate: productData.recipientCurrencyToSenderCurrencyExchangeRate || 1,
    amountPresets: fixedDenominations.length > 0 ? [] : [5000, 10000, 20000, 50000],
    allowCustomAmount: fixedDenominations.length === 0,
    currency: selectedCountry.value?.currency || 'FCFA',
    recipientCurrency: 'USD',
    requireEmail: true,
    defaultAmount: fixedDenominations[0]?.value || productData.minSenderDenomination || 5000
  };

  const result = await window.amountDialog(config);

  if (result.accepted) {
    router.push({
      path: '/checkout/quick',
      query: {
        productId: productData.productId,
        productName: productData.productName,
        amount: result.data.amount,
        email: result.data.email,
        orderType: 'gift_card',
        countryCode: selectedCountry.value?.countryCode || 'BJ'
      }
    });
  }
};

// Handle gift card click - opens amount dialog directly
const handleGiftCardClick = async (product) => {
  const productData = product.data || product;

  const fixedDenominations = productData.denominationType === 'FIXED'
    ? (productData.fixedSenderDenominations || []).map((val, idx) => ({
        value: val,
        recipientValue: productData.fixedRecipientDenominations?.[idx] || ''
      }))
    : [];

  const config = {
    title: 'Sélectionnez le montant',
    product: productData,
    denominationType: productData.denominationType,
    fixedDenominations: fixedDenominations,
    minAmount: productData.minSenderDenomination,
    maxAmount: productData.maxSenderDenomination,
    currency: selectedCountry.value?.currency || 'FCFA',
    recipientCurrency: 'USD',
    exchangeRate: productData.recipientCurrencyToSenderCurrencyExchangeRate || 1,
    requireEmail: true
  };

  const result = await window.amountDialog(config);

  if (result.accepted) {
    router.push({
      path: '/checkout/quick',
      query: {
        productId: productData.productId,
        productName: productData.productName,
        amount: result.data.amount,
        email: result.data.email,
        orderType: 'gift_card',
        countryCode: selectedCountry.value?.countryCode || 'BJ'
      }
    });
  }
};

// Handle eSim click - goes to product detail page
const handleEsimClick = async (product) => {
  const pkg = product.data || product;
  const country = selectedCountry.value || $stores.global.state.selectedCountry;

  if (!country) {
    // No country selected, redirect to esim page to select one
    router.push('/esim');
    return;
  }

  // Redirect to esim product detail page
  const slug = `${pkg.packageCode}-${country.countryCode.toLowerCase()}`;
  router.push(`/esim/${slug}`);
};

// Category slug mapping
const categorySlugMap = {
  'payment': 'payment-cards',
  'shopping': 'shopping-gift-cards',
  'gaming': 'gaming-cards',
  'entertainment': 'entertainment-gift-cards'
};

// Handle popular product click - routes to product detail page
const handlePopularProductClick = async (product) => {
  const productData = product.data || product;
  const country = selectedCountry.value || $stores.global.state.selectedCountry;
  const categoryKey = product.category || categoryKeyFromProduct(productData);

  // Check if this is a custom card
  if (productData.isCustom || (typeof productData.productId === 'string' && productData.productId.startsWith('card_'))) {
    router.push(`/custom-cards/${productData.productId}`);
    return;
  }

  const categorySlug = categorySlugMap[categoryKey] || 'shopping-gift-cards';

  // Redirect to gift card product detail page
  const slug = `${productData.productId}-${country?.countryCode?.toLowerCase() || 'bj'}`;
  router.push(`/cards/${categorySlug}/${slug}`);
};
</script>

<style scoped>
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* eSim Package Card Styles */
.package-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.package-card-inner {
  background: white;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.package-card:hover .package-card-inner {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border-color: rgba(1, 243, 122, 0.3);
  transform: translateY(-4px);
}

.package-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.package-data {
  font-size: 28px;
  font-weight: 700;
  color: #01F37A;
}

.package-duration {
  font-size: 14px;
  color: #666;
  background: #f0fdf4;
  padding: 4px 12px;
  border-radius: 12px;
}

.package-name {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 8px;
  flex-grow: 1;
}

.package-details {
  margin-bottom: 16px;
}

.package-speed {
  font-size: 13px;
  color: #666;
}

.package-price {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 16px;
}

.package-button {
  width: 100%;
  padding: 12px;
  background: linear-gradient(to right, #01F37A, #00D9FF);
  color: white;
  font-weight: 600;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.package-button:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 12px rgba(1, 243, 122, 0.3);
}
</style>
